import type { AuditLogFilters } from "src/api/client-api/auditLog/auditLogFilters";
import type { AuditLogAction, AuditLogDomain } from "src/api/client-api/auditLog/auditLogSchemas";
import { foldText } from "src/components/FacetFilters/foldText";

export const AUDIT_LOG_QUERY_FIELDS = [
    "action",
    "actor_type",
    "actor_id",
    "entity_type",
    "entity_id",
    "domain",
    "scope",
    "scope_id",
] as const;

export type AuditLogQueryField = (typeof AUDIT_LOG_QUERY_FIELDS)[number];

export type AuditLogQueryFilters = Pick<
    AuditLogFilters,
    | "action"
    | "action_not"
    | "actorType"
    | "actorType_not"
    | "actorId"
    | "actorId_not"
    | "entityType"
    | "entityType_not"
    | "entityId"
    | "domain"
    | "rootType"
    | "rootType_not"
    | "rootId"
    | "q"
>;

export const AUDIT_LOG_DROPPABLE_FIELDS = ["action", "actor_type", "actor_id", "entity_type", "scope"] as const;

export const isAuditLogDroppableField = (field: AuditLogQueryField): boolean =>
    (AUDIT_LOG_DROPPABLE_FIELDS as readonly string[]).includes(field);

const append = <T>(current: T[] | undefined, value: T): T[] =>
    current === undefined ? [value]
    : current.includes(value) ? current
    : [...current, value];

export type ParsedAuditLogQuery = {
    filters: AuditLogQueryFilters;
    unknownFields: string[];
    unknownValues: AuditLogQueryField[];
};

const ACTION_ALIASES: Record<string, AuditLogAction> = {
    create: "create",
    creation: "create",
    update: "update",
    modification: "update",
    delete: "delete",
    suppression: "delete",
};

const ACTOR_TYPE_ALIASES: Record<string, string> = {
    human: "human",
    humain: "human",
    service: "service",
    unattributed: "unattributed",
};

const DOMAIN_ALIASES: Record<string, AuditLogDomain> = {
    role: "role",
    roles: "role",
    integration: "integration",
    integrations: "integration",
    user: "user",
    users: "user",
    utilisateur: "user",
};

const SCOPE_INPUT_ALIASES: Record<string, string> = {
    organisation: "organization",
    plateforme: "platform",
};

const isQueryField = (value: string): value is AuditLogQueryField =>
    (AUDIT_LOG_QUERY_FIELDS as readonly string[]).includes(value);

const TOKEN = /(\S+:"(?:\\.|[^"\\])*"|"(?:\\.|[^"\\])*"|\S+:\S+|\S+)/g;

const unquote = (value: string): string => value.replace(/^"|"$/g, "").replace(/\\(["\\])/g, "$1");

export const tokenizeAuditLogQuery = (input: string): string[] => input.match(TOKEN) ?? [];

type AuditLogTokenParts = {
    rawField: string | null;
    value: string;
    isDropped: boolean;
};

const splitTokenParts = (token: string): AuditLogTokenParts => {
    const isDropped = token.startsWith("-") && token.length > 1;
    const body = isDropped ? token.slice(1) : token;

    if (body.startsWith('"')) {
        return { rawField: null, value: token, isDropped: false };
    }

    const separator = body.indexOf(":");

    return separator <= 0 ?
            { rawField: null, value: token, isDropped: false }
        :   { rawField: body.slice(0, separator), value: unquote(body.slice(separator + 1)).trim(), isDropped };
};

export type AuditLogQueryToken = {
    field: AuditLogQueryField | null;
    value: string;
    isDropped: boolean;
};

export const splitAuditLogQueryToken = (token: string): AuditLogQueryToken => {
    const { rawField, value, isDropped } = splitTokenParts(token);
    const field = rawField === null ? "" : foldText(rawField);

    return isQueryField(field) ? { field, value, isDropped } : { field: null, value: token, isDropped: false };
};

export const parseAuditLogQuery = (input: string): ParsedAuditLogQuery => {
    const filters: AuditLogQueryFilters = {};
    const unknownFields: string[] = [];
    const unknownValues: AuditLogQueryField[] = [];
    const freeText: string[] = [];

    for (const token of tokenizeAuditLogQuery(input)) {
        const { rawField, value: rawValue, isDropped } = splitTokenParts(token);

        if (rawField === null) {
            freeText.push(unquote(token));
            continue;
        }

        const field = foldText(rawField);

        if (!isQueryField(field)) {
            unknownFields.push(rawField);
            continue;
        }

        if (isDropped && !isAuditLogDroppableField(field)) {
            unknownFields.push(`-${rawField}`);
            continue;
        }

        if (rawValue === "") {
            unknownValues.push(field);
            continue;
        }

        const value = foldText(rawValue);

        if (field === "action") {
            const action = ACTION_ALIASES[value];

            if (action === undefined) {
                unknownValues.push(field);
            } else if (isDropped) {
                filters.action_not = append(filters.action_not, action);
            } else {
                filters.action = append(filters.action, action);
            }
            continue;
        }

        if (field === "actor_type") {
            const actorType = ACTOR_TYPE_ALIASES[value];

            if (actorType === undefined) {
                unknownValues.push(field);
            } else if (isDropped) {
                filters.actorType_not = append(filters.actorType_not, actorType);
            } else {
                filters.actorType = append(filters.actorType, actorType);
            }
            continue;
        }

        if (field === "domain") {
            const domain = DOMAIN_ALIASES[value];
            domain === undefined ? unknownValues.push(field) : (filters.domain = domain);
            continue;
        }

        if (field === "scope") {
            const scope = SCOPE_INPUT_ALIASES[value] ?? rawValue;

            isDropped ?
                (filters.rootType_not = append(filters.rootType_not, scope))
            :   (filters.rootType = append(filters.rootType, scope));
            continue;
        }

        if (field === "actor_id") {
            isDropped ?
                (filters.actorId_not = append(filters.actorId_not, rawValue))
            :   (filters.actorId = append(filters.actorId, rawValue));
            continue;
        }

        if (field === "scope_id") {
            filters.rootId = rawValue;
            continue;
        }

        if (field === "entity_id") {
            filters.entityId = rawValue;
            continue;
        }

        isDropped ?
            (filters.entityType_not = append(filters.entityType_not, rawValue))
        :   (filters.entityType = append(filters.entityType, rawValue));
    }

    if (freeText.length > 0) {
        filters.q = freeText.join(" ");
    }

    return { filters, unknownFields, unknownValues };
};

const needsQuotes = (value: string): boolean => /[\s:"\\]/.test(value);

const escapeQuoted = (value: string): string => value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');

export const quote = (value: string): string => (needsQuotes(value) ? `"${escapeQuoted(value)}"` : value);

export const buildAuditLogQuery = (filters: AuditLogQueryFilters): string => {
    const parts: string[] = [];

    const tokens = (field: string, values: string[] | undefined, prefix = "") =>
        (values ?? []).forEach((value) => parts.push(`${prefix}${field}:${quote(value)}`));

    const dimension = (field: string, kept: string[] | undefined, dropped: string[] | undefined) => {
        tokens(field, kept);
        tokens(field, dropped, "-");
    };

    dimension("action", filters.action, filters.action_not);
    dimension("actor_type", filters.actorType, filters.actorType_not);
    dimension("actor_id", filters.actorId, filters.actorId_not);
    dimension("entity_type", filters.entityType, filters.entityType_not);
    filters.entityId !== undefined && parts.push(`entity_id:${quote(filters.entityId)}`);
    filters.domain !== undefined && parts.push(`domain:${filters.domain}`);
    dimension("scope", filters.rootType, filters.rootType_not);
    filters.rootId !== undefined && parts.push(`scope_id:${quote(filters.rootId)}`);
    filters.q !== undefined && filters.q.trim() !== "" && parts.push(quote(filters.q.trim()));

    return parts.join(" ");
};
